import { useEffect, useRef } from 'react';
import IMapProps from './IMapProps';
import * as atlas from 'azure-maps-control';
import "azure-maps-control/dist/atlas.min.css";
import { makeStyles, mergeClasses } from '@fluentui/react-components';

const useStyles = makeStyles({
    mapContainer: {
        width: "40%",
        height: "400px",
        overflow: "hidden",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",

        "@media (max-width: 768px)": {
            width: "90%",
            height: "300px",
            borderRadius: "0"
        }
    },
    mapFrame: {
        width: "100%",
        height: "100%",
        border: 0,

        "@media (max-width: 768px)": {
            width: "100%",
            padding: "0 1rem"
        },

        "& .map-copyright, & .azure-map-copyright, & .azure-map-copyright-context": {
            display: "none !important"
        }
    },
    popup: {
        padding: "1rem"
    },
    rounded: {
        borderRadius: "50%"
    }
});

export default function Map (props: IMapProps) {
    const styles = useStyles();

    const mapRef = useRef(null);
    const mapInstanceRef = useRef<atlas.Map>(null!);

    useEffect(() => {
        if (mapRef.current && !mapInstanceRef.current) {
            mapInstanceRef.current = new atlas.Map(mapRef.current, {
                center: props.coordinates,
                zoom: props.zoom,
                view: "Auto",
                authOptions: {
                    authType: atlas.AuthenticationType.subscriptionKey,
                    subscriptionKey: process.env.REACT_APP_AZURE_MAPS_API_KEY
                },
                showLogo: false,
                showFeedbackLink: false,
                showZoomButtons: false
            });

            mapInstanceRef.current.events.add('ready', () => {
                mapInstanceRef.current.resize();

                if (props.marker) {
                    const marker = new atlas.HtmlMarker({
                        color: props.marker.color,
                        position: props.marker.position
                    });

                    mapInstanceRef.current.markers.add(marker);

                    if (props.popup) {
                        const content = document.createElement('div') as HTMLElement;
                        content.className = styles.popup;
                        content.innerHTML = props.popup.content as string;

                        const popup = new atlas.Popup({...props.popup, content});

                        mapInstanceRef.current.events.add('click', marker, () => {
                            popup.setOptions({
                                position: marker.getOptions().position
                            });

                            popup.open(mapInstanceRef.current);
                        });
                    }
                }
            });
        }

        return () => {
            if (mapInstanceRef.current) {
                mapInstanceRef.current.dispose();
                mapInstanceRef.current = null!;
            }
        };
    }, [props]);

    const containerClassName = props.isRounded
        ? mergeClasses(styles.mapContainer, styles.rounded)
        : styles.mapContainer;

    const mapClassName = props.isRounded
        ? mergeClasses(styles.mapFrame, styles.rounded)
        : styles.mapFrame;

    return (
        <div className={containerClassName}>
            <div ref={mapRef} className={mapClassName} />
        </div>
    );

}